import { useCallback, useMemo, useState } from "react";

import { Button, Callout } from "@blueprintjs/core";

import { StyledDialog } from "@/components/pieces/StyledDialog";
import {
  RubricDetailQuery,
  TargetType,
  useCopyEvaluationCriteriaToRubricMutation,
  useCopyEvaluationCriteriaToScenarioMutation,
} from "@/graphql";
import { formatErrors } from "@/utils/array";

import { PreviewCriteriaList } from "./PreviewCriteriaList";
import { RubricsController } from "./RubricsController";

type Rubric = RubricDetailQuery["rubric"];

interface ImportFromRubricDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  target: { type: TargetType; id: string };
}

export function ImportFromRubricDialog({
  open,
  onClose,
  onSuccess,
  target,
}: ImportFromRubricDialogProps) {
  const [selectedRubric, setSelectedRubric] = useState<Rubric | null>(null);
  const [selectedCriteriaIds, setSelectedCriteriaIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setSelectedRubric(null);
    setSelectedCriteriaIds([]);
    setError(null);
    onClose();
  }, [onClose]);

  const [copyEvaluationCriteriaToScenario] =
    useCopyEvaluationCriteriaToScenarioMutation();

  const [copyEvaluationCriteriaToRubric] =
    useCopyEvaluationCriteriaToRubricMutation();

  const excludedIds = useMemo(() => {
    if (target.type === TargetType.Rubric) {
      return [target.id];
    }
    return [];
  }, [target]);

  const handleAddToTarget = useCallback(() => {
    if (target.type === TargetType.Scenario) {
      copyEvaluationCriteriaToScenario({
        variables: {
          evaluationCriterionIds: selectedCriteriaIds,
          scenarioId: target.id,
        },
        onCompleted: ({ copyEvaluationCriteriaToScenario }) => {
          if (!copyEvaluationCriteriaToScenario?.ok) {
            const errorMessages = formatErrors(
              copyEvaluationCriteriaToScenario?.errors,
            );
            setError(errorMessages || "Failed to copy evaluation criteria");
          } else {
            onSuccess?.();
            handleClose();
          }
        },
        onError: (error) => {
          setError(error.message);
        },
      });
    } else {
      copyEvaluationCriteriaToRubric({
        variables: {
          evaluationCriterionIds: selectedCriteriaIds,
          rubricId: target.id,
        },
        onCompleted: ({ copyEvaluationCriteriaToRubric }) => {
          if (!copyEvaluationCriteriaToRubric?.ok) {
            const errorMessages = formatErrors(
              copyEvaluationCriteriaToRubric?.errors,
            );
            setError(errorMessages || "Failed to copy evaluation criteria");
          } else {
            onSuccess?.();
            handleClose();
          }
        },
        onError: (error) => {
          setError(error.message);
        },
      });
    }
  }, [
    selectedCriteriaIds,
    target,
    handleClose,
    onSuccess,
    copyEvaluationCriteriaToScenario,
    copyEvaluationCriteriaToRubric,
  ]);

  const handleBack = useCallback(() => {
    setError(null);
    setSelectedRubric(null);
    setSelectedCriteriaIds([]);
  }, []);

  const handleSelectRubric = useCallback((rubric: Rubric) => {
    setError(null);
    setSelectedRubric(rubric);
    setSelectedCriteriaIds([]);
  }, []);

  const handleSetSelectedCriteriaIds = useCallback((ids: string[]) => {
    setError(null);
    setSelectedCriteriaIds(ids);
  }, []);

  const isPreviewMode = selectedRubric !== null;
  const dialogTitle = isPreviewMode ? (
    <div className="flex items-center">
      <Button
        icon="arrow-left"
        minimal={true}
        small={true}
        onClick={handleBack}
        style={{ marginRight: 8 }}
      />
      <div className="text-lg">Preview Criteria</div>
    </div>
  ) : (
    <div className="text-lg">Import from Rubric</div>
  );

  return (
    <StyledDialog
      isOpen={open}
      onClose={handleClose}
      canOutsideClickClose={false}
      width="700px"
      title={dialogTitle}
    >
      {error && (
        <div className="m-3 mb-0">
          <Callout intent="danger" icon={null}>
            {error}
          </Callout>
        </div>
      )}

      {isPreviewMode && selectedRubric ? (
        <PreviewCriteriaList
          rubric={selectedRubric}
          selectedCriteriaIds={selectedCriteriaIds}
          onChangeSelectedCriteriaIds={handleSetSelectedCriteriaIds}
          onAddToTarget={handleAddToTarget}
          target={target}
        />
      ) : (
        <RubricsController
          onSelectRubric={handleSelectRubric}
          excludedIds={excludedIds}
        />
      )}
    </StyledDialog>
  );
}
